import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { useAuth } from '../../contexts/AuthContext';
import { flagSystemCapabilityApi } from '../../api/flagSystemApi';
import useFlagSystemCapability from '../../hooks/useFlagSystemCapability';
import { flagError } from '../../utils/flagSystem';
import { canReadAdminFlagSystemStatus } from '../../utils/flagSystemCapability';
import { Feedback, FlagCapabilityState } from './FlagSystemCommon';

const emptyEditor = { draft: null, error: '', message: '', conflict: false, saving: false };

const CustomerFlagSystemStatus = ({ customerId, customerName }) => {
  const { accessContext, isAuthReady, isLoggedIn } = useAuth();
  const token = localStorage.getItem('token');
  const authorized = Boolean(isAuthReady && isLoggedIn && token && canReadAdminFlagSystemStatus(accessContext));
  const capability = useFlagSystemCapability(authorized ? customerId : null);
  const scope = useMemo(() => ({ customerId, token, accessContext, authorized }), [customerId, token, accessContext, authorized]);
  const currentScope = useRef(scope);
  currentScope.current = scope;
  const mounted = useRef(false);
  const pending = useRef(null);
  const [state, setState] = useState({ ...emptyEditor, scope });
  const editor = state.scope === scope ? state : emptyEditor;
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  const current = () => mounted.current && currentScope.current === scope && localStorage.getItem('token') === token;
  const change = values => setState(previous => ({
    ...(previous.scope === scope ? previous : emptyEditor), scope, ...values,
  }));
  const known = ['enabled', 'disabled'].includes(capability.status);
  const desired = editor.draft === null ? capability.enabled : editor.draft;
  const editable = known && capability.canRetry && !capability.refreshing && !editor.saving && !editor.conflict;
  const reload = async () => {
    if (pending.current === scope) return;
    const data = await capability.refetch();
    if (data && current()) change({ draft: null, error: '', message: '', conflict: false });
  };
  const save = async () => {
    if (!editable || desired === capability.enabled || pending.current === scope) return;
    pending.current = scope;
    change({ saving: true, error: '', message: '' });
    try {
      await flagSystemCapabilityApi.update(Number(customerId), desired);
      if (!current()) return;
      const data = await capability.refetch();
      if (!current()) return;
      if (!data) {
        change({ draft: null, error: 'The setting was saved, but its current status could not be refreshed. Reload status before making another change.' });
      } else if (data.enabled !== desired) {
        change({ draft: null, error: 'The setting was saved, but the refreshed status differs from your change. Review the current status before saving again.' });
      } else {
        change({ draft: null, message: `Flag System ${data.enabled ? 'enabled' : 'disabled'} for this customer.` });
      }
    } catch (error) {
      if (!current()) return;
      const status = error.response && error.response.status;
      change({
        conflict: status === 409,
        error: status === 409 ? 'Another administrator changed this setting. Reload status before retrying.' :
          status === 400 ? 'The server rejected the enabled value. Reload status and try again.' :
            flagError(error, 'Unable to save Flag System access. Reload status before retrying.'),
      });
    } finally {
      if (pending.current === scope) pending.current = null;
      if (current()) change({ saving: false });
    }
  };
  if (!authorized) return null;
  return <section className="flag-member-card" aria-label="Customer Flag System status">
    <div className="flex align-items-center justify-content-between flex-wrap gap-2">
      <h5 className="m-0">Flag System{customerName ? ` - ${customerName}` : ''}</h5>
      <Button label={editor.conflict ? 'Reload Status' : 'Refresh Status'} icon="pi pi-refresh"
        className="p-button-text" onClick={reload} disabled={!capability.canRetry || capability.refreshing || editor.saving} />
    </div>
    {known ?
      <p className="mt-3 mb-0">Customer access: <Tag value={capability.enabled ? 'Enabled' : 'Disabled'} severity={capability.enabled ? 'success' : 'warning'} /></p> :
      <FlagCapabilityState capability={{ ...capability, refetch: reload }} />}
    <div className="flex align-items-center flex-wrap gap-3 my-3">
      <div className="form-check form-switch m-0">
        <input id="customer-flag-system-enabled" type="checkbox" role="switch"
          className="form-check-input flag-access-switch" checked={desired} aria-checked={desired} disabled={!editable}
          onChange={event => change({ draft: event.target.checked, error: '', message: '' })} />
        <label className="form-check-label" htmlFor="customer-flag-system-enabled">Enable Flag System for this customer</label>
      </div>
      <Button label="Save Flag System Access" icon="pi pi-check" onClick={save} loading={editor.saving}
        disabled={!editable || desired === capability.enabled} />
    </div>
    <Feedback error={editor.error} message={editor.message} />
    <p className="text-color-secondary mb-0">Disabling retains flag definitions, member assignments, overrides and history.
      This setting does not grant Admin users access to the separate flag-management APIs.</p>
  </section>;
};

export default CustomerFlagSystemStatus;
